import { timestamp as Timestamp } from '@harvesthq/clockwork'
import moment from 'moment'

import {
  forwardRef,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
  useImperativeHandle,
} from 'preact/compat'

import { toMoment, formattedTime } from '../lib/helpers/time'

const TimestampInputsComponent = ({ onChange }, ref) => {
  const startTimeInput = useRef(null)
  const [startTime, setStartTime] = useState()

  const endTimeInput = useRef(null)
  const [endTime, setEndTime] = useState()

  const totalTime = useMemo(() => {
    if (!startTime || !endTime) return

    const delta = endTime.diff(startTime)
    if (delta < 0) {
      return moment.duration(delta).add(24, 'hours')
    }
    return moment.duration(delta)
  }, [startTime, endTime])

  const formattedTotalTime = useMemo(() => {
    if (!totalTime) return ''
    return formattedTime(moment.duration(totalTime).asHours())
  }, [totalTime])

  const onEnterTime = (input, setter) => setter(toMoment(input.current?.value))

  const onSanitizeInput = (input, time, setter) => {
    if (time === undefined) {
      setter(undefined)

      if (input.current) {
        input.current.value = ''
      }
    } else if (input.current) {
      input.current.value = Timestamp.to_string(time)
    }
  }

  const clear = () => {
    if (startTimeInput.current) startTimeInput.current.value = ''
    if (endTimeInput.current) endTimeInput.current.value = ''
    setStartTime(undefined)
    setEndTime(undefined)
  }

  useImperativeHandle(ref, () => ({
    clear,
  }))

  const formatTimestamp = useCallback((time) => {
    if (!time) return undefined
    return Timestamp.to_string(time)
  }, [])

  useEffect(() => {
    if (startTime) {
      const startTimestamp = formatTimestamp(startTime)
      const endTimestamp = formatTimestamp(endTime)
      onChange(startTimestamp, endTimestamp)
    } else {
      onChange()
    }
  }, [startTime, endTime, onChange, formatTimestamp])

  return (
    <div className="pds-flex pds-items-center">
      <input
        type="text"
        className="pds-input pds-mb-sm input-timestamps"
        placeholder="Start time"
        onKeyUp={() => onEnterTime(startTimeInput, setStartTime)}
        onBlur={() => onSanitizeInput(startTimeInput, startTime, setStartTime)}
        ref={startTimeInput}
      />

      <span className="pds-inline-block pds-mb-xs pds-mx-sm pds-color-gray">
        to
      </span>

      <input
        type="text"
        className="pds-input pds-mb-sm input-timestamps"
        placeholder="End time"
        onKeyUp={() => onEnterTime(endTimeInput, setEndTime)}
        onBlur={() => onSanitizeInput(endTimeInput, endTime, setEndTime)}
        ref={endTimeInput}
      />

      <div
        data-testid="total-time"
        className="pds-px-sm pds-mb-xs pds-color-gray"
      >
        {formattedTotalTime}
      </div>
    </div>
  )
}

export const TimestampInputs = forwardRef(TimestampInputsComponent)
